import React from 'react';
import { Calendar, Check } from 'lucide-react';

interface SeasonalCalendarWidgetProps {
  produceName: string;
  seasonalMonths: number[]; // Array of month numbers (1-12)
  title?: string;
  showLegend?: boolean;
}

const SeasonalCalendarWidget: React.FC<SeasonalCalendarWidgetProps> = ({
  produceName,
  seasonalMonths = [],
  title,
  showLegend = true,
}) => {
  const displayTitle = title || `${produceName} Seasonal Calendar`;

  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const fullMonthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const isInSeason = (monthIndex: number): boolean => {
    return seasonalMonths.includes(monthIndex + 1);
  };

  const getSeasonalMessage = (): string => {
    if (seasonalMonths.length === 0) {
      return 'Availability information coming soon';
    }
    if (seasonalMonths.length === 12) {
      return `${produceName} is available year-round in Southwest Florida!`;
    }
    const monthNames = seasonalMonths
      .sort((a, b) => a - b)
      .map((m) => fullMonthNames[m - 1]);
    if (monthNames.length <= 3) {
      return `Peak season: ${monthNames.join(', ')}`;
    }
    return `Available ${monthNames.length} months per year`;
  };

  const styles = {
    widget: {
      backgroundColor: '#fff',
      borderRadius: '8px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      marginBottom: '24px',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '16px',
      paddingBottom: '12px',
      borderBottom: '2px solid #2D5A27',
    },
    headerIcon: {
      color: '#2D5A27',
      marginRight: '12px',
    },
    title: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1a1a1a',
      margin: 0,
    },
    message: {
      fontSize: '14px',
      color: '#666',
      marginBottom: '20px',
      padding: '12px',
      backgroundColor: '#f0f7ef',
      borderRadius: '6px',
      borderLeft: '3px solid #2D5A27',
    },
    calendarGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '10px',
      marginBottom: showLegend ? '20px' : '0',
    },
    monthCell: {
      position: 'relative' as const,
      aspectRatio: '1',
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '6px',
      border: '2px solid #e5e5e5',
      fontSize: '13px',
      fontWeight: '600',
      transition: 'all 0.3s ease',
      cursor: 'default',
    } as React.CSSProperties,
    monthCellActive: {
      backgroundColor: '#2D5A27',
      borderColor: '#2D5A27',
      color: '#fff',
    },
    monthCellInactive: {
      backgroundColor: '#f9f9f9',
      borderColor: '#e5e5e5',
      color: '#999',
    },
    checkIcon: {
      position: 'absolute' as const,
      top: '4px',
      right: '4px',
      width: '14px',
      height: '14px',
    },
    legend: {
      display: 'flex',
      gap: '16px',
      justifyContent: 'center',
      paddingTop: '16px',
      borderTop: '1px solid #e5e5e5',
      fontSize: '12px',
    },
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    legendBox: {
      width: '16px',
      height: '16px',
      borderRadius: '3px',
      border: '2px solid',
    },
    legendBoxActive: {
      backgroundColor: '#2D5A27',
      borderColor: '#2D5A27',
    },
    legendBoxInactive: {
      backgroundColor: '#f9f9f9',
      borderColor: '#e5e5e5',
    },
    empty: {
      textAlign: 'center' as const,
      padding: '24px',
      color: '#666',
      backgroundColor: '#f9f9f9',
      borderRadius: '6px',
    },
  };

  return (
    <div style={styles.widget}>
      <div style={styles.header}>
        <Calendar style={styles.headerIcon} size={24} />
        <h3 style={styles.title}>{displayTitle}</h3>
      </div>

      <div style={styles.message}>{getSeasonalMessage()}</div>

      {seasonalMonths.length > 0 ? (
        <>
          <div style={styles.calendarGrid}>
            {months.map((month, index) => {
              const inSeason = isInSeason(index);
              return (
                <div
                  key={month}
                  style={{
                    ...styles.monthCell,
                    ...(inSeason ? styles.monthCellActive : styles.monthCellInactive),
                  }}
                  title={
                    inSeason
                      ? `${fullMonthNames[index]} - In Season`
                      : `${fullMonthNames[index]} - Not typically available`
                  }
                >
                  {inSeason && (
                    <Check
                      size={14}
                      style={styles.checkIcon}
                      strokeWidth={3}
                    />
                  )}
                  {month}
                </div>
              );
            })}
          </div>

          {showLegend && (
            <div style={styles.legend}>
              <div style={styles.legendItem}>
                <div
                  style={{
                    ...styles.legendBox,
                    ...styles.legendBoxActive,
                  }}
                />
                <span>In Season</span>
              </div>
              <div style={styles.legendItem}>
                <div
                  style={{
                    ...styles.legendBox,
                    ...styles.legendBoxInactive,
                  }}
                />
                <span>Off Season</span>
              </div>
            </div>
          )}
        </>
      ) : (
        <div style={styles.empty}>
          <Calendar size={48} style={{ margin: '0 auto 12px', color: '#ccc' }} />
          <p>Seasonal availability information not yet available for {produceName}.</p>
          <p style={{ fontSize: '12px', marginTop: '8px' }}>
            Check with local farms for current availability.
          </p>
        </div>
      )}
    </div>
  );
};

export default SeasonalCalendarWidget;
